import { defineStore } from 'pinia';
import { getChatsCompleted } from '@/flagler-api/chat';

import { useGlobalAbility } from '@/composables/globalAbility';

type Goal = {
  value: number;
  label: string;
  reward: string;
};

export const rtmGoals: Goal[] = [
  {
    value: 100,
    label: '100',
    reward: '$50',
  },
  {
    value: 125,
    label: '125',
    reward: '$100',
  },
  {
    value: 150,
    label: '150',
    reward: '$150',
  },
  {
    value: 175,
    label: '175',
    reward: '$200',
  },
  {
    value: 200,
    label: '200',
    reward: '$250',
  },
];

export const useCareCounselorGoalsStore = defineStore(
  'CareCounselorGoalsStore',
  () => {
    const progressMade = ref<number>(0);
    const goalAchieved = ref<number>(0);
    const rtmProgress = ref<number>(0);
    const rtmNextGoalIndex = computed(() => {
      return rtmGoals.findIndex((goal) => goal.value > rtmProgress.value);
    });
    const rtmNextGoal = computed((): Goal | undefined => {
      return rtmNextGoalIndex.value >= 0
        ? rtmGoals[rtmNextGoalIndex.value]
        : undefined;
    });
    const deltaToRtmNextGoal = computed((): number => {
      if (rtmNextGoal.value) {
        return rtmNextGoal.value.value - rtmProgress.value;
      } else {
        return -1;
      }
    });
    const rtmNextProgressPercentage = computed(() => {
      if (rtmNextGoalIndex.value >= 0) {
        const previousGoal =
          rtmNextGoalIndex.value > 0
            ? rtmGoals[rtmNextGoalIndex.value - 1].value
            : 0;
        const nextGoal = rtmGoals[rtmNextGoalIndex.value];
        const progress =
          (rtmProgress.value - previousGoal) / (nextGoal.value - previousGoal);
        return Math.max(0, Math.min(100, progress * 100));
      } else {
        return 100;
      }
    });
    // we are currently refreshing goals manually at the following times:
    // 1. When a chat's status changes (see flagler-api/chat)
    // 2. When a message is sent (see flagler-api/message)
    // 3. When a phone call ends (see components/rtm/PhoneCallManager)
    // 4. When the rtm metrics forms are submitted (see components/rtm/RtmMetricsModal)
    const refreshGoals = async (celebrate = true) => {
      const ability = useGlobalAbility();

      if (ability && ability.can('read', 'Chat')) {
        const chatsCompleted = await getChatsCompleted();
        const rtmp = rtmProgress.value;
        rtmProgress.value = chatsCompleted.RTM + chatsCompleted.CCM;
        if (celebrate && rtmProgress.value > rtmp) {
          progressMade.value = progressMade.value + 1;
          if (rtmGoals.findIndex((g) => g.value === rtmProgress.value) >= 0) {
            goalAchieved.value = goalAchieved.value + 1;
          }
        }
      }
    };

    const startup = async () => {
      await refreshGoals(false);
    };

    const simulateGoalAchieved = () => {
      setTimeout(() => (goalAchieved.value = goalAchieved.value + 1), 1500);
    };

    return {
      // getters
      rtmProgress,
      rtmNextProgressPercentage,
      rtmNextGoal,
      deltaToRtmNextGoal,
      progressMade,
      goalAchieved,

      // actions
      refreshGoals,
      startup,
      simulateGoalAchieved,
    };
  }
);
